<template>
    <div :id="id" ref="modal" class="modal" tabindex="-1">
        <div class="modal-dialog" :class="modalDialogClass">
            <div class="modal-content">
                <div class="modal-header" :class="{centered: !closeable && centeredHeader}">
                    <span class="modal-title" v-html="title"></span>
                    <button
                        type="button"
                        class="close"
                        :disabled="!closeable"
                        :class="{
                            'd-none': !closeable,
                            tighter: tighterCloseButton,
                        }"
                        @click="onClose($event)"
                    >
                        <span aria-hidden="true"><i class="mdi mdi-close"></i></span>
                    </button>
                </div>

                <slot></slot>
            </div>
        </div>
    </div>
</template>

<script>
import '../modal';

export default {
    props: {
        id: {
            type: String,
            required: true,
        },
        centered: {
            type: Boolean,
            required: true,
        },
        title: {
            type: String,
            required: true,
        },
        show: {
            type: Boolean,
            required: true,
        },
        close: {
            type: Boolean,
            required: true,
        },
        size: {
            type: String,
            required: false,
            default() {
                return null;
            },
        },
        scrollable: {
            type: Boolean,
            required: false,
            default() {
                return true;
            },
        },
        closeable: {
            type: Boolean,
            required: false,
            default() {
                return true;
            },
        },
        tighterCloseButton: {
            type: Boolean,
            required: false,
            default() {
                return false;
            },
        },
        centeredHeader: {
            type: Boolean,
            required: false,
            default() {
                return false;
            },
        },
    },

    data() {
        return {
            isVisible: false,
        };
    },

    computed: {
        /**
         * Handle the class definition for modal dialog.
         *
         * @returns {*}
         * */
        modalDialogClass() {
            const classes = {
                'modal-dialog-centered': this.centered,
                'modal-dialog-scrollable': this.scrollable,
            };

            if (typeof this.size === 'string' && this.size.length) {
                classes[this.size] = true;
            }

            return classes;
        },
    },

    watch: {
        /**
         * Handle the watch of show status and show the modal if required.
         *
         * @returns {void}
         * */
        show() {
            if (!this.show || this.isVisible) {
                return;
            }

            this.$refs.modal.modal('show');
        },

        /**
         * Handle the watch of close status and hide the modal if required.
         *
         * @returns {void}
         * */
        close() {
            if (!this.close || !this.isVisible) {
                return;
            }

            this.onClose(null);
        },
    },

    methods: {
        /**
         * Handle the close call of modal.
         *
         * @param {MouseEvent} $event
         *
         * @return {void}
         */
        onClose($event) {
            if (typeof $event === 'object' && $event !== null) {
                $event.preventDefault();
            }

            if (!this.closeable || !this.isVisible) {
                return;
            }

            this.$refs.modal.modal('hide');
        },
    },

    beforeDestroy() {
        this.$refs.modal.removeEventListener('show.bs.modal', () => {
            this.isVisible = true;
            this.$emit('modal-opening');
        });
        this.$refs.modal.removeEventListener('shown.bs.modal', () => {
            this.$emit('modal-visible');
        });
        this.$refs.modal.removeEventListener('hide.bs.modal', (event) => {
            if (!this.closeable) {
                event.preventDefault();

                return false;
            }

            this.isVisible = false;
            this.$emit('modal-close');
        });
        this.$refs.modal.removeEventListener('hidden.bs.modal', () => {
            this.$emit('modal-invisible');
        });
    },

    mounted() {
        this.$refs.modal.addEventListener('show.bs.modal', () => {
            this.isVisible = true;
            this.$emit('modal-opening');
        });
        this.$refs.modal.addEventListener('shown.bs.modal', () => {
            this.$emit('modal-visible');
        });
        this.$refs.modal.addEventListener('hide.bs.modal', (event) => {
            if (!this.closeable) {
                event.preventDefault();

                return false;
            }

            this.isVisible = false;
            this.$emit('modal-close');
        });
        this.$refs.modal.addEventListener('hidden.bs.modal', () => {
            this.$emit('modal-invisible');
        });
    },
};
</script>

<style lang="scss">
    .centered {
        justify-content: center;
    }
</style>
